import { TrackOnLeave } from '@/components/TrackOnLeave';
import { medicalConditions } from '@/data/PreConsultation/medication-data';
import { FormTitleAndLogo } from '@/pages/components/Form';
import { MultiSelect } from '@/pages/components/MultiSelect/MultiSelect';
import type { Step } from '@montugroup/form-builder';

const SUBMIT_STEP_INDEX = 6;

export const triageSteps = ({ onStart }: { onStart: () => void }): Step[] => [
  {
    id: 1,
    type: 'section',
    route: '',
    formTitle: (
      <FormTitleAndLogo>
        Thank you, now we&apos;ll ask a few simple questions to match you to the right clinician.
      </FormTitleAndLogo>
    ),
    content: [
      {
        type: 'text',
        value: 'This will take less than a minute',
      },
      {
        type: 'custom',
        component: <TrackOnLeave onLeave={onStart} />,
      },
    ],
    nextButtonConfig: {
      text: 'Continue',
      metadata: {
        id: 'start',
      },
    },
  } as Step,
  {
    id: 2,
    type: 'question',
    route: 'medicare',
    qid: 'has_medicare',
    formTitle: <FormTitleAndLogo>Health Overview</FormTitleAndLogo>,
    title: 'Do you have a Medicare card?',
    breadcrumbs: [
      { label: 'Cards', status: 'current' },
      { label: 'Conditions', status: 'upcoming' },
      { label: 'Trialled meds', status: 'upcoming' },
      { label: 'Current meds', status: 'upcoming' },
    ],
    nextButtonConfig: {
      metadata: {
        id: 'has-medicare',
      },
    },
    content: [
      {
        type: 'radio',
        values: [
          { id: 1, label: 'Yes', value: true },
          { id: 2, label: 'No', value: false },
        ],
      },
    ],
    skipTo: (nextStepIndex: number, values: { [key: string]: any }) =>
      values.has_medicare === 'true' ? nextStepIndex : SUBMIT_STEP_INDEX,
  } as Step,
  {
    id: 3,
    type: 'question',
    route: 'concession-card',
    qid: 'has_concession_card',
    formTitle: <FormTitleAndLogo>Health Overview</FormTitleAndLogo>,
    title: 'Do you have a Commonwealth Concession card?',
    subtitle:
      'We accept Commonwealth issued healthcare cards, DVA cards, veterans cards and seniors health or pensioners cards.',
    breadcrumbs: [
      { label: 'Cards', status: 'current' },
      { label: 'Conditions', status: 'upcoming' },
      { label: 'Trialled meds', status: 'upcoming' },
      { label: 'Current meds', status: 'upcoming' },
    ],
    nextButtonConfig: {
      metadata: {
        id: 'has-concession-card',
      },
    },
    content: [
      {
        type: 'radio',
        values: [
          { id: 1, label: 'Yes', value: true },
          { id: 2, label: 'No', value: false },
        ],
      },
    ],
    skipTo: (nextStepIndex: number, values: { [key: string]: any }) =>
      values.has_concession_card === 'true' ? SUBMIT_STEP_INDEX : nextStepIndex,
  } as Step,
  {
    id: 4,
    type: 'question',
    route: 'conditions',
    qid: 'conditions',
    formTitle: <FormTitleAndLogo>Health Overview</FormTitleAndLogo>,
    title:
      'Please select the main condition(s) and/or symptom(s) you are seeking to address with alternative healthcare:',
    breadcrumbs: [
      { label: 'Cards', status: 'complete' },
      { label: 'Conditions', status: 'current' },
      { label: 'Trialled meds', status: 'upcoming' },
      { label: 'Current meds', status: 'upcoming' },
    ],
    nextButtonConfig: {
      metadata: {
        id: 'conditions',
      },
    },
    enableNextOnlyWhenValid: true,
    content: [
      {
        type: 'custom',
        name: 'conditions',
        component: <MultiSelect label="Search for conditions" name="conditions.concerns" options={medicalConditions} />,
      },
    ],
    skipTo: (nextStepIndex: number, values: { [key: string]: any }) => {
      const conditionsSelected = values.conditions?.concerns?.length ?? 0;

      return conditionsSelected < 3 ? nextStepIndex : SUBMIT_STEP_INDEX;
    },
  } as Step,
  {
    id: 5,
    type: 'question',
    route: 'trialled-medication',
    qid: 'has_trialled_medication',
    formTitle: <FormTitleAndLogo>Health Overview</FormTitleAndLogo>,
    title: 'Have you previously trialled any prescription medication(s) to treat the condition(s) you selected?',
    subtitle: 'Please do **not** include over-the-counter (OTC) medications.',
    breadcrumbs: [
      { label: 'Cards', status: 'complete' },
      { label: 'Conditions', status: 'complete' },
      { label: 'Trialled meds', status: 'current' },
      { label: 'Current meds', status: 'upcoming' },
    ],
    nextButtonConfig: {
      metadata: {
        id: 'has-trialled-medication',
      },
    },
    content: [
      {
        type: 'radio',
        values: [
          { id: 1, label: 'Yes', value: true },
          { id: 2, label: 'No', value: false },
        ],
      },
    ],
    skipTo: (nextStepIndex: number, values: { [key: string]: any }) =>
      values.has_trialled_medication === 'true' ? nextStepIndex : SUBMIT_STEP_INDEX,
  } as Step,
  {
    id: 6,
    type: 'question',
    route: 'current-medications',
    qid: 'current_medication_count',
    formTitle: <FormTitleAndLogo>Health Overview</FormTitleAndLogo>,
    title: 'How many prescription medications are you currently taking?',
    subtitle: 'Please do **not** include over-the-counter (OTC) medications.',
    breadcrumbs: [
      { label: 'Cards', status: 'complete' },
      { label: 'Conditions', status: 'complete' },
      { label: 'Trialled meds', status: 'complete' },
      { label: 'Current meds', status: 'current' },
    ],
    nextButtonConfig: {
      metadata: {
        id: 'current-medication-count',
      },
    },
    content: [
      {
        type: 'radio',
        values: [
          { id: 1, label: '0 - 3 prescription medications', value: '0-3' },
          { id: 2, label: 'More than 3 prescription medications', value: '4+' },
        ],
      },
    ],
    skipTo: (_nextStepIndex: number, values: { [key: string]: any }) =>
      values.current_medication_count === '0-3' ? SUBMIT_STEP_INDEX : SUBMIT_STEP_INDEX,
  } as Step,
  {
    id: 7,
    type: 'section',
    shouldSave: true,
    hideBackButton: true,
    nextButtonConfig: {
      text: '',
    },
    content: [
      {
        type: 'text',
        value: '',
      },
    ],
  } as Step,
];
